import { StoryblokEditable } from '@activebrands/core-web/libs/storyblok/storyblok';
import responsiveTextStyles from '@activebrands/core-web/utils/responsive-text-styles';
import { Styles } from '@activebrands/core-web/utils/style-object-to-props';
import Button, { ButtonProps } from '@activebrands/core-web/components/buttons/Button';
import { getButtonTheme, sizes, themes } from './theme-button-config';

export interface ThemeButtonProps extends Omit<ButtonProps, 'availableSizes' | 'theme'> {
    fontKeys?: string | (string | null)[];
    isSelected?: boolean;
    isVolatile?: boolean;
    replace?: boolean;
    sbEditable?: StoryblokEditable;
    theme?: string | string[];
    to?: string;
}

const ThemeButton = (props: ThemeButtonProps) => {
    const {
        $style = {},
        size = 'md',
        theme = 'default',
        fontKeys = 'Miscellaneous/18_100_-1',
        active,
        as,
        role,
        'aria-expanded': ariaExpanded,
        'aria-pressed': ariaPressed,
        sbEditable,
        ...rest
    } = props;

    const textStyles = responsiveTextStyles(fontKeys as Styles);

    const supportsAriaPressed =
        (as == null || as === 'button') &&
        !['tab', 'option', 'menuitem', 'radio'].includes(role ?? '') &&
        ariaExpanded == null;

    let explicitAriaPressed;
    if ('aria-pressed' in props) {
        explicitAriaPressed = ariaPressed;
    } else if (active !== undefined) {
        explicitAriaPressed = active;
    }

    const ariaPressedResolved = supportsAriaPressed ? explicitAriaPressed : undefined;

    return (
        <Button
            $style={{
                borderRadius: '100px',
                borderStyle: 'solid',
                borderWidth: '1px',
                display: 'inline-block',
                textAlign: 'center',
                transitionDuration: 'var(--duration-fast)',
                transitionProperty: 'all',
                transitionTimingFunction: 'var(--ease)',
                ...textStyles,
                ...$style,
            }}
            availableSizes={sizes}
            size={size}
            theme={themes[getButtonTheme(theme as string)]}
            active={active}
            as={as}
            role={role}
            aria-expanded={ariaExpanded}
            {...sbEditable}
            {...rest}
            {...(ariaPressedResolved !== undefined ? { 'aria-pressed': ariaPressedResolved } : {})}
        />
    );
};

export default ThemeButton;
